import React from 'react';
import { BarChartIcon, LightbulbIcon } from '@bizfirst/common-react';
import { ExecutionObserverConfig } from '../models/ExecutionObserverConfig';
import '../styles/PreviewSection.css';

interface PreviewSectionProps {
  config: ExecutionObserverConfig;
  stats: {
    totalEvents: number;
    matchingEvents: number;
    filteredOutEvents: number;
    matchPercentage: number;
  };
}

/**
 * Preview Section - Shows statistics and filter summary
 *
 * Displays:
 * - Total events vs matching events
 * - Match percentage
 * - Active filters summary
 * - Sample event preview
 */
export const PreviewSection: React.FC<PreviewSectionProps> = ({
  config,
  stats
}) => {
  const getFilterSummary = (): string[] => {
    const summary: string[] = [];

    if (!config.enabled) {
      summary.push('Observer disabled - no events observed');
      return summary;
    }

    summary.push(`Detail level: ${config.detailLevel || 'detailed'}`);

    const filter = config.filter;
    if (!filter) return summary;

    // Entity types
    const entityTypes = [];
    if (filter.entityTypes?.includeProcess) entityTypes.push('Process');
    if (filter.entityTypes?.includeThread) entityTypes.push('Thread');
    if (filter.entityTypes?.includeNode) entityTypes.push('Node');
    if (filter.entityTypes?.includeConfigResolution) entityTypes.push('Config');
    if (filter.entityTypes?.includeExpressions) entityTypes.push('Expression');
    if (filter.entityTypes?.includePolicies) entityTypes.push('Policy');

    if (entityTypes.length > 0) {
      summary.push(`Entity types: ${entityTypes.join(', ')}`);
    }

    // Event types
    const eventTypes = [];
    if (filter.eventTypes?.errorsOnly) {
      summary.push('Event types: Errors only');
    } else {
      if (filter.eventTypes?.includeStarting) eventTypes.push('Starting');
      if (filter.eventTypes?.includeExecuting) eventTypes.push('Executing');
      if (filter.eventTypes?.includeCompleted) eventTypes.push('Completed');
      if (filter.eventTypes?.includeErrored) eventTypes.push('Errored');

      if (eventTypes.length > 0) {
        summary.push(`Event types: ${eventTypes.join(', ')}`);
      }
    }

    // Entity IDs
    const ids = [];
    if (filter.entityIds?.processIds?.length) {
      ids.push(`Processes: ${filter.entityIds.processIds.join(',')}`);
    }
    if (filter.entityIds?.executionId) {
      ids.push(`Execution: ${filter.entityIds.executionId}`);
    }
    if (filter.entityIds?.nodeKeys?.length) {
      ids.push(`Nodes: ${filter.entityIds.nodeKeys.join(',')}`);
    }
    if (ids.length > 0) {
      summary.push(...ids);
    }

    // Performance
    if (filter.performance) {
      if (filter.performance.minDurationMs !== undefined) {
        summary.push(`Min duration: ≥${filter.performance.minDurationMs}ms`);
      }
      if (filter.performance.maxDurationMs !== undefined) {
        summary.push(`Max duration: ≤${filter.performance.maxDurationMs}ms`);
      }
      if (filter.performance.slowThresholdMs !== undefined) {
        summary.push(`Slow threshold: >${filter.performance.slowThresholdMs}ms`);
      }
    }

    return summary;
  };

  const filterSummary = getFilterSummary();

  return (
    <section className="preview-section">
      <h3><BarChartIcon size={18} /> Preview & Statistics</h3>

      {/* Statistics Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.totalEvents}</div>
          <div className="stat-label">Total Events</div>
          <div className="stat-subtext">in execution</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{stats.matchingEvents}</div>
          <div className="stat-label">Matching</div>
          <div className="stat-subtext">will be observed</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{stats.filteredOutEvents}</div>
          <div className="stat-label">Filtered Out</div>
          <div className="stat-subtext">will be hidden</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{stats.matchPercentage.toFixed(1)}%</div>
          <div className="stat-label">Match Rate</div>
          <div className="stat-subtext">events observed</div>
        </div>
      </div>

      {/* Filter Summary */}
      <div className="filter-summary">
        <h4>Active Filters:</h4>
        {filterSummary.length > 0 ? (
          <ul className="filter-list">
            {filterSummary.map((item, i) => (
              <li key={i}>
                <span className="filter-item">{item}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="no-filters">No filters applied - all events will be observed</p>
        )}
      </div>

      {/* Matching Events Preview */}
      <div className="events-preview">
        <h4>Sample Matching Events:</h4>
        <div className="event-list">
          {stats.matchingEvents > 0 ? (
            <>
              <div className="event-item">
                <span className="event-type">Process</span>
                <span className="event-stage">Starting</span>
                <span className="event-time">2026-06-18 10:30:45</span>
              </div>
              <div className="event-item">
                <span className="event-type">Thread</span>
                <span className="event-stage">Executing</span>
                <span className="event-time">2026-06-18 10:30:47</span>
              </div>
              <div className="event-item">
                <span className="event-type">Node</span>
                <span className="event-stage">Completed</span>
                <span className="event-time">2026-06-18 10:30:50</span>
              </div>
              {stats.matchingEvents > 3 && (
                <div className="event-item placeholder">
                  <em>... and {stats.matchingEvents - 3} more events</em>
                </div>
              )}
            </>
          ) : (
            <p className="no-events">No events match current filters</p>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="preview-help">
        <p>
          <LightbulbIcon size={14} /> <strong>Preview is simulated.</strong> During actual execution, this will show
          real events based on your filter configuration.
        </p>
      </div>
    </section>
  );
};

export default PreviewSection;
